import React, { useState } from 'react';
import axios from 'axios';
import { Phone, MapPin, Edit3, X, Check, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Inline parent-contact + address editor for a single student row.
 * Used in the Head Teacher's Student Directory.
 * Saving calls PUT /api/head-teacher/students/{id}/contact.
 */
const StudentContactEditor = ({ student, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    parent_name: student.parent_name || '',
    parent_mobile: student.parent_mobile || '',
    address: student.address || '',
  });

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/head-teacher/students/${student.id}/contact`, form);
      toast.success('Contact updated');
      setEditing(false);
      onSaved && onSaved({ ...student, ...form });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  if (!editing) {
    const hasMobile = !!student.parent_mobile;
    const hasAddress = !!student.address;
    return (
      <div className="flex flex-col gap-1 text-xs">
        <div className={`flex items-center gap-1 ${hasMobile ? 'text-gray-700' : 'text-amber-600'}`} data-testid={`ht-parent-mobile-${student.id}`}>
          <Phone size={12} />
          {hasMobile ? (
            <span>+91 {student.parent_mobile} {student.parent_name && <span className="text-gray-400">· {student.parent_name}</span>}</span>
          ) : (
            <span className="italic">No parent mobile</span>
          )}
        </div>
        <div className={`flex items-start gap-1 ${hasAddress ? 'text-gray-600' : 'text-amber-600'}`} data-testid={`ht-address-${student.id}`}>
          <MapPin size={12} className="mt-0.5 shrink-0" />
          <span className="line-clamp-2 max-w-[260px]">{hasAddress ? student.address : <em className="italic">No address</em>}</span>
        </div>
        <button
          onClick={() => setEditing(true)}
          className="text-blue-600 hover:underline self-start inline-flex items-center gap-1 mt-1"
          data-testid={`ht-edit-contact-${student.id}`}
        >
          <Edit3 size={11} /> Edit
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-1.5 bg-blue-50 border border-blue-200 rounded p-2 min-w-[280px]" data-testid={`ht-contact-editor-${student.id}`}>
      <input
        type="text"
        value={form.parent_name}
        onChange={e => setForm({ ...form, parent_name: e.target.value })}
        placeholder="Parent / Guardian name"
        className="w-full text-xs border rounded px-2 py-1"
        data-testid="ht-edit-parent-name"
      />
      <input
        type="tel"
        value={form.parent_mobile}
        onChange={e => setForm({ ...form, parent_mobile: e.target.value })}
        placeholder="10-digit mobile (98765 43210)"
        maxLength={15}
        className="w-full text-xs border rounded px-2 py-1"
        data-testid="ht-edit-parent-mobile"
      />
      <textarea
        value={form.address}
        onChange={e => setForm({ ...form, address: e.target.value })}
        placeholder="Full address (door no, street, city, PIN)"
        rows={3}
        className="w-full text-xs border rounded px-2 py-1"
        data-testid="ht-edit-address"
      />
      <div className="flex gap-1 justify-end">
        <Button size="sm" variant="ghost" onClick={() => setEditing(false)} className="h-6 px-2 text-xs">
          <X size={12} />
        </Button>
        <Button size="sm" onClick={save} disabled={saving} className="h-6 px-2 text-xs bg-blue-600 hover:bg-blue-700">
          {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} className="mr-1" />}
          Save
        </Button>
      </div>
    </div>
  );
};

export default StudentContactEditor;
